import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Descriptions,
    Image,
    Input,
    Modal,
    Space,
    Spin,
    Tag,
    Typography,
    message,
} from 'antd';
import {
    CheckOutlined,
    DislikeOutlined,
    LikeOutlined,
    PlayCircleOutlined,
    SendOutlined,
    StarFilled,
    SyncOutlined,
} from '@ant-design/icons';
import { axiosInstance } from '../../config';
import { formatUsdCost } from '../BananasGPT/utils/money';
import ReviewReplyGenerator from './ReviewReplyGenerator';
import VideoPlayer from '../reviews/VideoPlayer';

const { TextArea } = Input;

const REVIEW_STATUS_META = {
    NEW: { label: 'Новый', color: 'orange' },
    UNPROCESSED: { label: 'Новый', color: 'orange' },
    VIEWED: { label: 'Просмотрен', color: 'blue' },
    PROCESSED: { label: 'Обработан', color: 'green' },
};

const ORDER_STATUS_LABELS = {
    DELIVERED: 'Доставлен',
    CANCELLED: 'Отменен',
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const productImageUrl = (product = {}) => {
    const primary = product.primary_image;
    if (typeof primary === 'string' && primary) return primary;
    if (Array.isArray(primary) && primary[0]) return primary[0];
    if (product.cover_image) return product.cover_image;
    const images = product.images;
    if (Array.isArray(images) && images[0]) {
        return typeof images[0] === 'string' ? images[0] : images[0].url;
    }
    return null;
};

const commentStatusMeta = (answer = {}) => {
    const status = String(answer.status || '').toUpperCase();
    if (answer.is_rejected || status === 'REJECTED' || status === 'DECLINED') {
        return { label: 'Отклонён', color: 'error', pending: false };
    }
    if (answer.is_published || ['PUBLISHED', 'MODERATED', 'PROCESSED'].includes(status)) {
        return { label: 'Опубликован', color: 'success', pending: false };
    }
    if (status === 'PENDING') {
        return { label: 'Отправляется', color: 'processing', pending: true };
    }
    return { label: 'На модерации', color: 'warning', pending: true };
};

const generatingLabel = (dots) => `Генерирую ответ${'.'.repeat(dots)}`;

const ReviewViewModal = ({ reviewId, open, onClose, onUpdated }) => {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generatingDots, setGeneratingDots] = useState(1);
    const [detail, setDetail] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [generationCost, setGenerationCost] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [watchingPublication, setWatchingPublication] = useState(false);
    const [closing, setClosing] = useState(false);
    const [marking, setMarking] = useState(false);
    const [pendingReply, setPendingReply] = useState(null);
    const pollTimerRef = useRef(null);
    const watchingRef = useRef(false);

    const stopPublicationWatch = () => {
        watchingRef.current = false;
        setWatchingPublication(false);
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    const refreshFromOzon = async ({ persist = false } = {}) => {
        const { data } = await axiosInstance.get(
            `/v1/reviews/${encodeURIComponent(reviewId)}`,
            { params: { refresh: true, persist } }
        );
        setDetail(data);
        return data;
    };

    useEffect(() => {
        if (!generating) {
            setGeneratingDots(1);
            return undefined;
        }
        const timer = setInterval(() => {
            setGeneratingDots((current) => (current % 4) + 1);
        }, 400);
        return () => clearInterval(timer);
    }, [generating]);

    useEffect(() => {
        if (!open || !reviewId) return undefined;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setDetail(null);
            setReplyText('');
            setGenerationCost(null);
            setGenerating(false);
            setWatchingPublication(false);
            setPendingReply(null);
            setClosing(false);
            setMarking(false);
            watchingRef.current = false;
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
            try {
                const { data } = await axiosInstance.get(`/v1/reviews/${encodeURIComponent(reviewId)}`);
                if (!cancelled) setDetail(data);
            } catch (error) {
                if (!cancelled) {
                    message.error(
                        error.response?.data?.detail || 'Не удалось загрузить отзыв'
                    );
                    onClose?.();
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
        // onClose намеренно не в зависимостях — иначе перезагрузка на каждый ререндер родителя
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reviewId]);

    const sendReply = async () => {
        const text = replyText.trim();
        if (generating || sending) return;
        if (!text) {
            message.warning('Введите текст ответа');
            return;
        }
        const buyerText = [detail?.text?.comment, detail?.text?.positive, detail?.text?.negative]
            .some((value) => String(value || '').trim());
        if (!buyerText) {
            message.warning(
                'Ozon не принимает ответы на отзывы без текста покупателя'
            );
            return;
        }
        setSending(true);
        try {
            const { data } = await axiosInstance.post(
                `/v1/reviews/${encodeURIComponent(reviewId)}/reply`,
                { text, mark_as_processed: true }
            );
            setDetail(data);
            setReplyText('');
            setGenerationCost(null);
            setPendingReply({ text, commentId: data?.answers?.[0]?.uuid || null });
            message.success('Ответ отправлен в Ozon');
            watchingRef.current = true;
            setWatchingPublication(true);
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            pollTimerRef.current = setInterval(async () => {
                if (!watchingRef.current) return;
                try {
                    await refreshFromOzon({ persist: false });
                } catch (error) {
                    // оставляем последний известный статус, следующая попытка через интервал
                }
            }, 5000);
            try {
                await refreshFromOzon({ persist: false });
            } catch (error) {
                // первый опрос не критичен — статус обновится на следующем цикле
            }
        } catch (error) {
            message.error(error.response?.data?.detail || 'Не удалось отправить ответ');
        } finally {
            setSending(false);
        }
    };

    const handleClose = async () => {
        if (closing) return;
        const shouldPersist = watchingRef.current;
        stopPublicationWatch();
        setClosing(true);
        try {
            if (shouldPersist && reviewId) {
                const data = await refreshFromOzon({ persist: true });
                onUpdated?.(data);
            }
        } catch (error) {
            message.warning(
                error.response?.data?.detail ||
                    'Не удалось сохранить отзыв в базу, окно будет закрыто'
            );
        } finally {
            setClosing(false);
            setPendingReply(null);
            onClose?.();
        }
    };

    const markProcessed = async () => {
        if (marking || generating) return;
        setMarking(true);
        try {
            const { data } = await axiosInstance.post(
                `/v1/reviews/${encodeURIComponent(reviewId)}/mark-processed`
            );
            setDetail(data);
            message.success('Отзыв отмечен обработанным');
            onUpdated?.(data);
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось отметить отзыв обработанным'
            );
        } finally {
            setMarking(false);
        }
    };

    const product = detail?.product || {};
    const productName =
        product.name || product.title || detail?.product_name || 'Товар';
    const offerId = product.offer_id || '—';
    const imageUrl = productImageUrl(product);
    const statusMeta = REVIEW_STATUS_META[detail?.status] || {
        label: detail?.status || '—',
        color: 'default',
    };
    const text = detail?.text || {};
    const buyerHasText = [text.comment, text.positive, text.negative].some((value) =>
        String(value || '').trim()
    );
    const alreadyProcessed = String(detail?.status || '').toUpperCase() === 'PROCESSED';
    const photos = detail?.photos || [];
    const videos = detail?.videos || [];
    const answers = useMemo(() => {
        const items = Array.isArray(detail?.answers) ? detail.answers : [];
        if (!pendingReply?.text) return items;
        const hasSame = items.some((answer) => {
            if (pendingReply.commentId && answer.uuid === pendingReply.commentId) {
                return true;
            }
            return (
                (answer.is_owner || answer.is_official) &&
                String(answer.text || '').trim() === pendingReply.text
            );
        });
        if (hasSame) return items;
        return [
            {
                uuid: pendingReply.commentId || 'pending-local',
                text: pendingReply.text,
                author: { name: 'Официальный представитель' },
                is_official: true,
                is_owner: true,
                is_published: false,
                is_rejected: false,
                status: 'PENDING',
            },
            ...items,
        ];
    }, [detail, pendingReply]);

    return (
        <>
            <Modal
                title="Отзыв на товар"
                open={open}
                onCancel={handleClose}
                footer={null}
                width={860}
                centered
                destroyOnClose
                maskClosable={!sending && !closing}
                rootClassName="review-view-modal"
                styles={{ body: { paddingTop: 12 } }}
            >
                {loading || !detail ? (
                    <div className="flex justify-center py-16">
                        <Spin size="large" />
                    </div>
                ) : (
                    <div className="review-view-modal-layout">
                        <div className="review-view-modal-scroll space-y-5">
                        <div className="flex gap-3">
                            {imageUrl ? (
                                <Image
                                    src={imageUrl}
                                    width={72}
                                    height={72}
                                    className="rounded object-cover"
                                    preview
                                />
                            ) : (
                                <div className="flex h-[72px] w-[72px] items-center justify-center rounded bg-black/5 text-xs text-black/40">
                                    Нет фото
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <Typography.Title level={5} style={{ margin: 0 }}>
                                    {productName}
                                </Typography.Title>
                                <Typography.Text type="secondary">
                                    Артикул: {offerId}
                                </Typography.Text>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center gap-1">
                                        <StarFilled style={{ color: '#faad14' }} />
                                        {Number(detail.rating || 0)}
                                    </span>
                                    <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                                </div>
                            </div>
                        </div>

                        <div>
                            <Typography.Text strong>Общая информация</Typography.Text>
                            <Descriptions
                                size="small"
                                bordered
                                column={{ xs: 1, sm: 2, md: 3 }}
                                className="mt-2"
                            >
                                <Descriptions.Item label="Оценка">
                                    <span className="inline-flex items-center gap-1">
                                        {Number(detail.rating || 0)}
                                        <StarFilled style={{ color: '#faad14' }} />
                                    </span>
                                </Descriptions.Item>
                                <Descriptions.Item label="Статус">
                                    <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Публикация">
                                    {formatDateTime(detail.published_at)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Статус заказа">
                                    {ORDER_STATUS_LABELS[detail.order_status] ||
                                        detail.order_status ||
                                        '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="SKU">
                                    {detail.sku || product.sku || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Заказ">
                                    {detail.order_number || detail.order_id || '—'}
                                </Descriptions.Item>
                            </Descriptions>
                        </div>

                        <div>
                            <Typography.Text strong>Отзыв</Typography.Text>
                            <div className="mt-2 rounded-lg border border-black/10 bg-black/[0.02] p-3">
                                <Typography.Text type="secondary">
                                    Покупатель:{' '}
                                    {detail.author_name ||
                                        'Пользователь предпочёл скрыть свои данные'}
                                </Typography.Text>
                                {text.positive ? (
                                    <div className="mt-2">
                                        <Typography.Text type="secondary">Достоинства</Typography.Text>
                                        <div>{text.positive}</div>
                                    </div>
                                ) : null}
                                {text.negative ? (
                                    <div className="mt-2">
                                        <Typography.Text type="secondary">Недостатки</Typography.Text>
                                        <div>{text.negative}</div>
                                    </div>
                                ) : null}
                                <div className="mt-2 whitespace-pre-wrap">
                                    {text.comment || 'Без текста'}
                                </div>
                                {(photos.length > 0 || videos.length > 0) && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {photos.map((photo, index) => (
                                            <Image
                                                key={`photo-${index}`}
                                                src={photo.url}
                                                width={96}
                                                height={96}
                                                className="rounded object-cover"
                                            />
                                        ))}
                                        {videos.map((video, index) => (
                                            <button
                                                key={`video-${video.uploadId || index}`}
                                                type="button"
                                                className="relative h-24 w-24 overflow-hidden rounded border-0 bg-black/80 p-0"
                                                onClick={() => setSelectedVideo(video)}
                                            >
                                                {video.preview_url || video.previewUrl ? (
                                                    <img
                                                        src={video.preview_url || video.previewUrl}
                                                        alt=""
                                                        className="h-full w-full object-cover opacity-80"
                                                    />
                                                ) : null}
                                                <span className="absolute inset-0 flex items-center justify-center text-white">
                                                    <PlayCircleOutlined style={{ fontSize: 28 }} />
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-3 flex items-center gap-4 text-black/55">
                                    <span className="inline-flex items-center gap-1">
                                        <LikeOutlined />
                                        {Number(detail.likes_amount || 0)}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <DislikeOutlined />
                                        {Number(detail.dislikes_amount || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <Typography.Text strong>Ответы</Typography.Text>
                                {watchingPublication ? (
                                    <Tag icon={<SyncOutlined spin />} color="processing">
                                        Проверяем публикацию…
                                    </Tag>
                                ) : null}
                            </div>
                            {answers.length === 0 ? (
                                <div className="mt-2 text-sm text-black/45">Ответов пока нет</div>
                            ) : (
                                <div className="mt-2 space-y-3">
                                    {answers.map((answer, index) => {
                                        const statusMeta = commentStatusMeta(answer);
                                        return (
                                        <div
                                            key={answer.uuid || index}
                                            className="rounded-lg border border-[#91caff]/60 bg-[#e6f4ff]/50 p-3"
                                        >
                                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                                <Typography.Text strong>
                                                    {answer.author?.name ||
                                                        (answer.is_official || answer.is_owner
                                                            ? 'Официальный представитель'
                                                            : 'Покупатель')}
                                                </Typography.Text>
                                                {answer.is_official || answer.is_owner ? (
                                                    <Tag color="blue">Продавец</Tag>
                                                ) : null}
                                                <Typography.Text type="secondary" className="text-xs">
                                                    {formatDateTime(answer.published_at)}
                                                </Typography.Text>
                                            </div>
                                            <div className="whitespace-pre-wrap">{answer.text}</div>
                                            <div className="mt-2">
                                                <Tag
                                                    icon={
                                                        watchingPublication && statusMeta.pending ? (
                                                            <SyncOutlined spin />
                                                        ) : undefined
                                                    }
                                                    color={statusMeta.color}
                                                >
                                                    {statusMeta.label}
                                                </Tag>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-black/10 pt-4">
                            <ReviewReplyGenerator
                                reviewId={reviewId}
                                review={detail}
                                generating={generating}
                                onGenerating={(next) => {
                                    setGenerating(next);
                                    if (next) setGenerationCost(null);
                                }}
                                onGenerated={(text, meta) => {
                                    setReplyText(text);
                                    const cost = meta?.cost;
                                    setGenerationCost(
                                        cost != null && Number.isFinite(Number(cost))
                                            ? Number(cost)
                                            : null
                                    );
                                }}
                            />
                        </div>
                        </div>

                        <div className="review-view-modal-reply">
                            <Typography.Text strong>Ответ на отзыв</Typography.Text>
                            {!buyerHasText ? (
                                <Alert
                                    className="mt-2"
                                    type="warning"
                                    showIcon
                                    message="Ozon не принимает ответы на отзывы без текста"
                                    description="У покупателя только оценка, без комментария. Публичный ответ отправить нельзя, отзыв можно отметить обработанным."
                                />
                            ) : null}
                            <TextArea
                                className={`mt-2${generating ? ' review-reply-generating' : ''}`}
                                autoSize={{ minRows: 4 }}
                                maxLength={3000}
                                showCount={!generating}
                                readOnly={generating}
                                value={generating ? generatingLabel(generatingDots) : replyText}
                                onChange={(event) => {
                                    if (!generating) setReplyText(event.target.value);
                                }}
                                placeholder="Напишите ответ покупателю…"
                            />
                            {generationCost != null ? (
                                <div className="mt-2 text-sm text-black/55">
                                    Стоимость ответа: {formatUsdCost(generationCost)}
                                </div>
                            ) : null}
                            <div className="mt-3 flex justify-end">
                                <Space>
                                    <Button onClick={handleClose} loading={closing}>
                                        Закрыть
                                    </Button>
                                    {!buyerHasText && !alreadyProcessed ? (
                                        <Button
                                            icon={<CheckOutlined />}
                                            loading={marking}
                                            disabled={generating || closing}
                                            onClick={markProcessed}
                                        >
                                            Отметить обработанным
                                        </Button>
                                    ) : null}
                                    <Button
                                        type="primary"
                                        icon={<SendOutlined />}
                                        loading={sending}
                                        disabled={generating || closing || !buyerHasText}
                                        onClick={sendReply}
                                    >
                                        Отправить ответ
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={Boolean(selectedVideo)}
                onCancel={() => setSelectedVideo(null)}
                footer={null}
                width={800}
                title="Просмотр видео"
                destroyOnClose
                centered
            >
                {selectedVideo ? (
                    <div
                        style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            maxHeight: 'calc(100vh - 200px)',
                            overflow: 'hidden',
                        }}
                    >
                        <VideoPlayer
                            url={selectedVideo.url}
                            poster={selectedVideo.preview_url || selectedVideo.previewUrl}
                        />
                    </div>
                ) : null}
            </Modal>
        </>
    );
};

export default ReviewViewModal;
